import {
  ConditionLogicId,
  DynamicFormType,
  IBrand,
  IConditionLogicImplementation,
  IDynamicForm,
  IInvoiceScopeData,
  isPatient,
} from '@principle-theorem/principle-core/interfaces';
import { TypeGuard, WithRef, isObject } from '@principle-theorem/shared';
import { isEqual, isString } from 'lodash';

const isInvoiceScopeData = TypeGuard.interface<IInvoiceScopeData>({
  patient: isPatient,
  medicalHistoryLink: isString,
  referrer: TypeGuard.noGuard(),
  invoice: isObject,
  confirmLink: isString,
  practice: isObject,
});

export interface IInvoiceIsPaidConditionConfig {
  isPaid: boolean;
}

export class InvoiceIsPaidCondition
  implements
    IConditionLogicImplementation<
      IInvoiceScopeData,
      IInvoiceIsPaidConditionConfig,
      boolean
    >
{
  conditionId = ConditionLogicId.InvoiceIsPaid;
  description = 'Invoice must be Paid';
  isValidContext = isInvoiceScopeData;

  isTrue(
    config: IInvoiceIsPaidConditionConfig,
    context: IInvoiceScopeData
  ): boolean {
    const isPaid = !!context.invoice.paidAt;
    return isPaid === config.isPaid;
  }

  async getForm(
    _brand: WithRef<IBrand>,
    initialValue?: IInvoiceIsPaidConditionConfig
  ): Promise<IDynamicForm<boolean>> {
    const isPaid = {
      type: DynamicFormType.Select,
      label: 'Is Paid?',
      options: [
        { label: 'Paid', value: true },
        { label: 'Not Paid', value: false },
      ],
      initialValue: initialValue?.isPaid ?? true,
      compareWith: isEqual,
    };
    return Promise.resolve({ isPaid });
  }

  async getSummary(
    config: IInvoiceIsPaidConditionConfig
  ): Promise<string | undefined> {
    return Promise.resolve(`Invoice is ${config.isPaid ? 'Paid' : 'Not Paid'}`);
  }
}

export interface IInvoiceIsOverdueConditionConfig {
  isOverdue: boolean;
}

export class InvoiceIsOverdueCondition
  implements
    IConditionLogicImplementation<
      IInvoiceScopeData,
      IInvoiceIsOverdueConditionConfig,
      boolean
    >
{
  conditionId = ConditionLogicId.InvoiceIsOverdue;
  description = 'Invoice must be Overdue';
  isValidContext = isInvoiceScopeData;

  isTrue(
    config: IInvoiceIsOverdueConditionConfig,
    context: IInvoiceScopeData
  ): boolean {
    const isOverdue = !!context.invoice.paidAt;
    return isOverdue === config.isOverdue;
  }

  async getForm(
    _brand: WithRef<IBrand>,
    initialValue?: IInvoiceIsOverdueConditionConfig
  ): Promise<IDynamicForm<boolean>> {
    const isOverdue = {
      type: DynamicFormType.Select,
      label: 'Is Overdue?',
      options: [
        { label: 'Overdue', value: true },
        { label: 'Not Overdue', value: false },
      ],
      initialValue: initialValue?.isOverdue ?? true,
      compareWith: isEqual,
    };
    return Promise.resolve({ isOverdue });
  }

  async getSummary(
    config: IInvoiceIsOverdueConditionConfig
  ): Promise<string | undefined> {
    return Promise.resolve(
      `Invoice is ${config.isOverdue ? 'Overdue' : 'Not Overdue'}`
    );
  }
}
